/* eslint-disable jsx-a11y/prefer-tag-over-role */
import { Icon, Tooltip } from "@clarkinc/zest-design-system";
import { logException } from "@wss/error-tracking";
import React, { useState } from "react";
import { TypeAnimation } from "react-type-animation";
import submitFeedback from "./submitFeedback";
import ChildOrNull from "~/common/ChildOrNull";
import ResourceLink from "./ResourceLink";
import { DocumentSummary } from "./Models";
interface ChatbotAnswerAndFeedbackProps {
  answer: string;
  feedback: string;
  interactionKey: string;
  itemNumber: string;
  relevantDocuments?: Readonly<DocumentSummary[]>;
  setFeedback: React.Dispatch<React.SetStateAction<string>>;
  setError: React.Dispatch<React.SetStateAction<string>>;
}
const ChatbotAnswerAndFeedback = ({
  answer,
  feedback,
  interactionKey,
  itemNumber,
  relevantDocuments,
  setFeedback,
  setError
}: ChatbotAnswerAndFeedbackProps) => {
  const maxCharsPerLine = 100;
  const maxLinesToShow = 8;
  const shortAnswerLength = 480;
  const maxAnswerLength = maxCharsPerLine * maxLinesToShow;
  const [showFullAnswer, setShowFullAnswer] = useState(answer.length <= maxAnswerLength);
  const [finishedTyping, setFinishedTyping] = useState(false);
  async function saveFeedback(feedbackType: string, interactionKey: string) {
    const response = await submitFeedback(interactionKey, feedbackType);
    if (response.isError) {
      await logException(response.error);
      setError(response.error.message);
      return;
    }
    setFeedback(feedbackType);
  }
  const answerToDisplay = showFullAnswer ? answer : `${answer.slice(0, shortAnswerLength)}...`;
  const animateAnswer = answer.length < maxAnswerLength || !showFullAnswer;
  return <>
      <div className="px-5 pt-5">
        <div className="text-sm sm:text-base font-normal pt-1 whitespace-pre-wrap max-w-[66rem]" data-testid="pdp-chatbot-answer">
          <button type="button" className="btn w-[72px] h-[21px] text-xs mb-3 pl-0.5 pr-0 py-0 pointer-events-none text-white tracking-[0.150em] chat-ask-button-gradient" style={{
          fontFamily: "inherit"
        }}>
            ANSWER
          </button>
          {animateAnswer ? <div>
              <TypeAnimation className="text-sm sm:text-base block min-h-[68px]" splitter={str => str.split(/(?= )/)} sequence={[answerToDisplay, () => setFinishedTyping(true)]} cursor={false} speed={{
            type: "keyStrokeDelayInMs",
            value: 25
          }} omitDeletionAnimation />
            </div> : <div className="text-sm sm:text-base block min-h-[68px]">
              {answerToDisplay}
            </div>}
        </div>
        <ChildOrNull condition={finishedTyping} data-sentry-element="ChildOrNull" data-sentry-source-file="ChatbotAnswerAndFeedback.tsx">
          {!showFullAnswer && <button type="button" className="text-sm sm:text-base font-semibold text-green-500 underline bg-white border-none p-0" onClick={() => setShowFullAnswer(true)}>
              Show full answer
            </button>}
          {!!relevantDocuments?.length && <div className={`${!showFullAnswer && "mt-2.5"}`} data-testid="resources-links-row">
              {relevantDocuments?.map(doc => <ResourceLink {...doc}>{doc.type}</ResourceLink>)}
            </div>}
          {!feedback && <div className="text-sm font-normal py-6">
              <Tooltip content="Helpful" contentId="feedbackHelpful" placement="top" theme="wss">
                <button type="button" className="border-none bg-white" onClick={() => saveFeedback("positive", interactionKey)}>
                  <Icon name="ri-thumb-up-line" className="align-text-bottom" size="xsmall" theme="wss" data-testid="pdp-chatbot-helpful" />
                </button>
              </Tooltip>
              <Tooltip content="Not Helpful" contentId="feedbackNotHelpful" placement="top" theme="wss">
                <button type="button" className="border-none bg-white" onClick={() => saveFeedback("negative", interactionKey)}>
                  <Icon name="ri-thumb-down-line" className="pl-2 mr-4 align-text-bottom" size="xsmall" theme="wss" data-testid="pdp-chatbot-not-helpful" />
                </button>
              </Tooltip>
              <div className="inline-block">
                AI-generated answer. Help us improve.
              </div>
            </div>}
          {feedback === "positive" && <div className="text-sm font-semibold py-6" data-testid="pdp-chatbot-helpful-selected">
              <Icon name="ri-thumb-up-fill" className="mr-2 align-text-bottom" size="xsmall" theme="wss" />
              Thank you for the feedback!
            </div>}
          {feedback === "negative" && <div className="text-sm font-semibold py-6" data-testid="pdp-chatbot-not-helpful-selected">
              <Icon name="ri-thumb-down-fill" className="mr-2 align-text-bottom" size="xsmall" theme="wss" />
              Thank you for the feedback.
            </div>}
        </ChildOrNull>
      </div>
      {finishedTyping && <div className="flex justify-center h-12 bg-gray-50 rounded-b-lg" data-testid="pdp-chatbot-assistance">
          <Icon name="ri-question-line" className="mt-4 mr-1.5" size="xsmall" theme="wss" />
          <div className="text-sm font-semibold text-gray-800 mt-3.5">
            Still need assistance?
          </div>
          <a href={`/ask.html?item=${itemNumber}#askAQuestion`} className="btn btn-small h-3 ml-4 mt-2.5 text-gray-800 font-semibold border-gray-800" data-testid="pdp-contact-us-button" style={{
        fontFamily: "inherit"
      }}>
            Contact Us
          </a>
        </div>}
    </>;
};
export default ChatbotAnswerAndFeedback;